import { Fragment, useState, type ReactNode } from 'react';

import { Icon } from '../Icon';
import { Select } from '../Select';
import styles from './Pagination.module.css';

export type PaginationSize = 'default' | 'small';
export type PaginationVariant = 'plain' | 'background';
export type PaginationAlign = 'start' | 'center' | 'end';
export type PaginationItemType = 'page' | 'prev' | 'next';

export interface PaginationProps {
  align?: PaginationAlign;
  ariaLabel?: string;
  className?: string;
  defaultCurrent?: number;
  defaultPageSize?: number;
  disabled?: boolean;
  hideOnSinglePage?: boolean;
  itemRender?: (page: number, type: PaginationItemType, element: ReactNode) => ReactNode;
  onChange?: (page: number, pageSize: number) => void;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (page: number, pageSize: number) => void;
  onShowSizeChange?: (page: number, pageSize: number) => void;
  page?: number;
  pageSize?: number;
  pageSizeOptions?: readonly number[];
  showQuickJumper?: boolean | { goButton?: ReactNode };
  showLessItems?: boolean;
  showSizeChanger?: boolean;
  showTitle?: boolean;
  showTotal?: boolean | ((total: number, range: [number, number]) => ReactNode);
  simple?: boolean | { readOnly?: boolean };
  size?: PaginationSize;
  total: number;
  totalBoundaryShowSizeChanger?: number;
  variant?: PaginationVariant;
}

type PageItem = number | 'ellipsis-start' | 'ellipsis-end';

/** Figma 的 `<5page` / `>5page` / `Both ends omitted` 三种形态：首页尾页常驻，当前页左右各一页，其余省略。 */
export function buildPageItems(page: number, pageCount: number, siblingCount = 1): PageItem[] {
  if (pageCount <= 7) return Array.from({ length: pageCount }, (_, index) => index + 1);

  const items: PageItem[] = [1];
  const start = page <= 4 && siblingCount > 0 ? 2 : page >= pageCount - 3 && siblingCount > 0 ? pageCount - 4 : Math.max(2, page - siblingCount);
  const end = page >= pageCount - 3 && siblingCount > 0 ? pageCount - 1 : page <= 4 && siblingCount > 0 ? Math.max(5, page + siblingCount) : Math.min(pageCount - 1, page + siblingCount);
  if (start > 2) items.push('ellipsis-start');

  for (let current = start; current <= end; current += 1) items.push(current);
  if (end < pageCount - 1) items.push('ellipsis-end');

  items.push(pageCount);
  return items;
}

export function Pagination({ align, ariaLabel = '分页', className, defaultCurrent = 1, defaultPageSize = 10, disabled = false, hideOnSinglePage = false, itemRender, onChange, onPageChange, onPageSizeChange, onShowSizeChange, page, pageSize, pageSizeOptions = [10, 20, 50, 100], showQuickJumper, showLessItems = false, showSizeChanger, showTitle = true, showTotal, simple = false, size = 'default', total, totalBoundaryShowSizeChanger = 0, variant = 'plain' }: PaginationProps) {
  const [uncontrolledPage, setUncontrolledPage] = useState(defaultCurrent);
  const [uncontrolledPageSize, setUncontrolledPageSize] = useState(defaultPageSize);
  const [simpleInputValue, setSimpleInputValue] = useState(String(defaultCurrent));
  const activePageSize = pageSize ?? uncontrolledPageSize;
  const pageCount = Math.max(1, Math.ceil(total / activePageSize));
  const visiblePage = Math.min(Math.max(page ?? uncontrolledPage, 1), pageCount);
  const [quickJumpValue, setQuickJumpValue] = useState('');
  const items = buildPageItems(visiblePage, pageCount, showLessItems ? 0 : 1);
  const iconSize = size === 'small' ? 'xs' : 'sm';
  const range: [number, number] = total === 0 ? [0, 0] : [(visiblePage - 1) * activePageSize + 1, Math.min(visiblePage * activePageSize, total)];
  const totalContent = typeof showTotal === 'function' ? showTotal(total, range) : `共 ${total.toLocaleString('zh-CN')} 条`;
  const quickJumperConfig = typeof showQuickJumper === 'object' ? showQuickJumper : undefined;
  const simpleConfig = typeof simple === 'object' ? simple : undefined;
  const isSimple = Boolean(simple);
  const shouldShowSizeChanger = showSizeChanger ?? (!isSimple && total > totalBoundaryShowSizeChanger);
  const shouldShowTotal = showTotal ?? !isSimple;
  const shouldShowQuickJumper = showQuickJumper ?? !isSimple;
  const changePage = (nextPage: number) => {
    if (disabled) return;
    const next = Math.min(Math.max(nextPage, 1), pageCount);
    if (page === undefined) setUncontrolledPage(next);
    setSimpleInputValue(String(next));
    onPageChange?.(next);
    onChange?.(next, activePageSize);
  };
  const changePageSize = (nextPageSize: number) => {
    if (disabled) return;
    if (pageSize === undefined) setUncontrolledPageSize(nextPageSize);
    if (page === undefined) setUncontrolledPage(1);
    onPageSizeChange?.(1, nextPageSize);
    onShowSizeChange?.(1, nextPageSize);
    onChange?.(1, nextPageSize);
  };
  const commitQuickJump = () => {
    const nextPage = Number.parseInt(quickJumpValue, 10);
    if (Number.isFinite(nextPage)) changePage(nextPage);
    setQuickJumpValue('');
  };

  if (hideOnSinglePage && pageCount <= 1) return null;

  const previousButton = <button aria-label="上一页" className={styles.control} disabled={disabled || visiblePage <= 1} onClick={() => changePage(visiblePage - 1)} title={showTitle ? '上一页' : undefined} type="button">
    <Icon name="a-arrow-left" size={iconSize} />
  </button>;
  const nextButton = <button aria-label="下一页" className={styles.control} disabled={disabled || visiblePage >= pageCount} onClick={() => changePage(visiblePage + 1)} title={showTitle ? '下一页' : undefined} type="button">
    <Icon name="a-arrow-right" size={iconSize} />
  </button>;

  return (
    <nav aria-label={ariaLabel} className={[styles.root, className].filter(Boolean).join(' ')} data-align={align} data-size={size} data-variant={variant}>
      {shouldShowTotal ? <span className={styles.total}>{totalContent}</span> : null}
      {shouldShowSizeChanger ? <Select ariaLabel="每页条数" className={styles.sizeChanger} disabled={disabled} onValueChange={(value) => changePageSize(Number(value))} options={pageSizeOptions.map((value) => ({ label: `${value} /页`, value: String(value) }))} size={size === 'small' ? 'small' : 'default'} value={String(activePageSize)} /> : null}
      <span className={styles.pages}>
      {itemRender ? itemRender(visiblePage - 1, 'prev', previousButton) : previousButton}
      {isSimple ? <span className={styles.simpleCurrent}>{simpleConfig?.readOnly ? visiblePage : <input aria-label="简洁分页当前页" disabled={disabled} inputMode="numeric" min={1} onChange={(event) => setSimpleInputValue(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter') changePage(Number.parseInt(simpleInputValue, 10)); }} type="number" value={page === undefined ? simpleInputValue : String(visiblePage)} />} / {pageCount}</span> : items.map((item) => (typeof item === 'number'
        ? <Fragment key={item}>{itemRender ? itemRender(item, 'page', <button aria-current={item === visiblePage ? 'page' : undefined} aria-label={`第 ${item} 页`} className={styles.item} data-selected={item === visiblePage || undefined} disabled={disabled} onClick={() => changePage(item)} title={showTitle ? `第 ${item} 页` : undefined} type="button">{item}</button>) : <button aria-current={item === visiblePage ? 'page' : undefined} aria-label={`第 ${item} 页`} className={styles.item} data-selected={item === visiblePage || undefined} disabled={disabled} onClick={() => changePage(item)} title={showTitle ? `第 ${item} 页` : undefined} type="button">{item}</button>}</Fragment>
        : <span aria-hidden="true" className={styles.more} key={item}>···</span>))}
      {itemRender ? itemRender(visiblePage + 1, 'next', nextButton) : nextButton}
      </span>
      {shouldShowQuickJumper ? <span className={styles.quickJumper}>跳至<input aria-label="跳至页码" disabled={disabled} inputMode="numeric" min={1} onChange={(event) => setQuickJumpValue(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter') commitQuickJump(); }} type="number" value={quickJumpValue} />页{quickJumperConfig?.goButton ? <button className={styles.goButton} disabled={disabled} onClick={commitQuickJump} type="button">{quickJumperConfig.goButton}</button> : null}</span> : null}
    </nav>
  );
}
